import styled from '@emotion/styled';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import ArrowIcon from 'assets/icons/arrow-down.svg';
import { colors } from 'constants/colors';
import { MultipleExportOptions } from 'pages/Structures/StructuresList/exportStructuresOptions';

// Stessa styled component di AdditionalExportsSelect.jsx (a sua volta
// replicata da components/form/Select/Select.jsx, non esportata da lì) —
// duplicata di nuovo qui, invece che importata da AdditionalExportsSelect,
// per restare un componente a sé stante e non accoppiare i due campi
// Admin fra loro. Stesse regole esatte, incluso il fix dell'altezza (vedi
// commento sotto) — necessarie per restare visivamente identica al campo
// "Export addizionali - Singolo ponte" accanto a cui compare sempre.
const CustomFormControl = styled(FormControl)(props => ({
  '& svg': {
    backgroundImage: props.disabled ? null : `url(${ArrowIcon})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    fill: 'none',
    backgroundSize: '60%',
  },
  '& label': { fontSize: '0.8rem', fontFamily: 'Ubuntu' },
  '& label.Mui-focused': {
    color: colors.BLACK,
    fontWeight: 'bold',
  },
  // I fratelli TextField-based sono più alti perché il tema (theme.js,
  // override MuiTextField) applica `.MuiOutlinedInput-root { height: '100%' }`
  // — che li fa allungare fino all'altezza della riga Grid (align-items:
  // stretch di default). Quella regola vive scoperta lì SOLO dentro
  // MuiTextField/MuiAutocomplete, non su MuiSelect/MuiFormControl in
  // generale: un Select "nudo" come questo non la eredita e resta alla sua
  // altezza naturale, più bassa — stessa regola replicata qui.
  '& .MuiOutlinedInput-root': {
    height: '100%',
  },
}));

// Admin > Gestione Utenti > Dettaglio Utente, campo "Export addizionali -
// Elenco ponti" (export MULTIPLO, sezione ponti — ExportStructuresPanel.jsx/
// StructuresList.jsx, pulsante "Esporta Elenco Ponti"). Componente dedicato,
// gemello di AdditionalExportsSelect.jsx (singolo ponte) ma più semplice:
// il catalogo bulk (MultipleExportOptions, 5 voci) non ha intestazioni di
// categoria né una differenziazione per tipologia utente — mostra sempre
// tutte e 5 le voci, in entrambi i form Ente/Professionista.
//
// `disabled` — progettista 2026-10-02: per un Ente il campo non ha senso
// (l'accesso di default al pannello bulk è già tutto-o-niente per quella
// tipologia, vedi ExportStructuresPanel.jsx) e va disabilitato, non
// nascosto — passato esplicitamente dal chiamante (InstitutionForm.jsx lo
// passa sempre `true`, essendo il form esclusivo di Ente).
const AdditionalExportsMultipleSelect = ({ disabled }) => {
  const { control } = useFormContext();
  const name = 'additionalExportsMultiple';
  const valueWatch = useWatch({ control, name });
  const theme = useTheme();

  // Stesso pattern di RHFMultiSelect/AdditionalExportsSelect: stato locale
  // `options` aggiornato sincronamente dentro `handleChange` insieme a
  // `field.onChange`, non solo derivato da `useWatch` — altrimenti la voce
  // appena cliccata non risulta evidenziata finché il menu non si
  // richiude/riapre.
  const [options, setOptions] = useState([]);

  useEffect(() => {
    setOptions(
      valueWatch === '' || valueWatch === null || valueWatch === undefined
        ? []
        : valueWatch
    );
  }, [valueWatch]);

  const handleChange = (event, field) => {
    const { value } = event.target;
    const nextValue = typeof value === 'string' ? value.split(',') : value;
    field.onChange(nextValue);
    setOptions(nextValue);
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field }) => (
        <CustomFormControl style={{ width: '100%' }} disabled={disabled}>
          <InputLabel style={{ top: '0px' }} id="additional-exports-multiple-label">
            Export addizionali - Elenco ponti
          </InputLabel>
          <Select
            {...field}
            multiple
            disabled={disabled}
            label="Export addizionali - Elenco ponti"
            labelId="additional-exports-multiple-label"
            value={options}
            onChange={event => handleChange(event, field)}
            renderValue={values => (
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {values.map(value => (
                  <Chip
                    key={value}
                    label={
                      MultipleExportOptions.find(option => option.value === value)
                        ?.label
                    }
                    size="small"
                    color="primary"
                  />
                ))}
              </Box>
            )}
          >
            {MultipleExportOptions.map(option => {
              // Evidenziazione: il colore va su un Box INTERNO alla
              // MenuItem, mai sulla MenuItem stessa — il tema (theme.js,
              // MuiMenuItem) impone `backgroundColor: colors.WHITE !important`
              // su ogni `.MuiMenuItem-root` dell'app, che vince su qualunque
              // sx/bgcolor messo direttamente sulla MenuItem.
              const isSelected = options?.some(el => el === option.value);
              return (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ padding: 0, pl: 2 }}
                >
                  <Box
                    width={'100%'}
                    p={0.5}
                    bgcolor={isSelected ? theme.palette.primary.light : ''}
                  >
                    {option.label}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </CustomFormControl>
      )}
    />
  );
};

AdditionalExportsMultipleSelect.propTypes = {
  disabled: PropTypes.bool,
};

export { AdditionalExportsMultipleSelect };
